/**
 * Detects ``data-kin-support-handoff`` parts in AI SDK UI messages (PS-1024 TDD 5.4).
 *
 * The agent emits this part when Kin decides a human should take over. It is an *offer* the user
 * can act on, not an instruction to the client: the widget-era ``action: "open_devrev_plug"``
 * was retired together with the DevRev PLuG (PS-1361), and the ticket is now created server-side
 * when the offer is accepted. The payload is keyed on ``variant``, decided by the server-resolved
 * entitlement (PS-1360):
 *
 * - ``ticket_offer`` (PAID, PREMIUM): carries ``offer_id``, the assistant ``messageId`` that the
 *   accept route takes back. The offer *is* the persisted message, so acceptance never depends on
 *   re-classifying a later free-text "yes".
 * - ``email_card`` (FREE): carries ``support_email`` and nothing to accept.
 *
 * Must match the agent SSE bridge (``openai_agents_sse._support_handoff_platform_sse``); the two
 * change together.
 */

import type { UIMessagePartsLike } from './data-suggestions';

const PART_TYPE = 'data-kin-support-handoff';

/** Mirror of the platform ``EffectiveEntitlement`` ladder (FREE < PAID < PREMIUM, CP-3739). */
export type SupportHandoffEntitlement = 'FREE' | 'PAID' | 'PREMIUM';

export type SupportHandoffTicketOffer = {
  variant: 'ticket_offer';
  entitlement: SupportHandoffEntitlement;
  /** Assistant ``messageId`` to send back on ``POST .../support-handoff``. */
  offerId: string;
};

export type SupportHandoffEmailCard = {
  variant: 'email_card';
  entitlement: SupportHandoffEntitlement;
  supportEmail: string;
};

export type SupportHandoffData = SupportHandoffTicketOffer | SupportHandoffEmailCard;

const ENTITLEMENTS: ReadonlySet<string> = new Set(['FREE', 'PAID', 'PREMIUM']);

function parseEntitlement(raw: unknown, fallback: SupportHandoffEntitlement): SupportHandoffEntitlement {
  return typeof raw === 'string' && ENTITLEMENTS.has(raw) ? (raw as SupportHandoffEntitlement) : fallback;
}

function nonEmptyString(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.trim().length > 0 ? raw : undefined;
}

function parseSupportHandoffPart(data: unknown): SupportHandoffData | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as { variant?: unknown; entitlement?: unknown; offer_id?: unknown; support_email?: unknown };
  if (d.variant === 'ticket_offer') {
    const offerId = nonEmptyString(d.offer_id);
    if (!offerId) return undefined;
    // The server only offers a ticket to PAID / PREMIUM; an unreadable tier still describes a
    // valid offer, so default to the lowest tier that can hold one.
    return { variant: 'ticket_offer', entitlement: parseEntitlement(d.entitlement, 'PAID'), offerId };
  }
  if (d.variant === 'email_card') {
    const supportEmail = nonEmptyString(d.support_email);
    if (!supportEmail) return undefined;
    return { variant: 'email_card', entitlement: parseEntitlement(d.entitlement, 'FREE'), supportEmail };
  }
  return undefined;
}

/** Returns the handoff offer on the message, or ``undefined`` when no part has a known ``variant``. */
export function getSupportHandoffData(message: UIMessagePartsLike): SupportHandoffData | undefined {
  const parts = message.parts;
  if (!parts?.length) return undefined;
  for (const p of parts) {
    if (p.type !== PART_TYPE) continue;
    const parsed = parseSupportHandoffPart(p.data);
    if (parsed) return parsed;
  }
  return undefined;
}

/** True when the message carries a renderable handoff part (either variant). */
export function hasSupportHandoff(message: UIMessagePartsLike): boolean {
  return getSupportHandoffData(message) !== undefined;
}

/**
 * Whether the always-available "Talk to a Human" control is shown (TDD 5.4, ruled 2026-08-26).
 * PREMIUM only: PAID (GROWTH) gets human support only through a Kin-initiated offer, so the
 * persistent control must never render for it even though the paid Figma frames show one
 * (known design gap, PS-1350). This is a UX gate; the accept route enforces entitlement itself.
 */
export function showPersistentSupportControl(entitlement: SupportHandoffEntitlement): boolean {
  return entitlement === 'PREMIUM';
}
